import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  runTransaction,
  Unsubscribe
} from 'firebase/firestore';
import { db } from './firebase';
import { CollectionChangeRequest, EmailSettings, ImpactStats, Product, Reservation, ReservationStatus, SiteSettings } from '../types';
import {
  sendReservationNotificationEmail,
  sendCollectionDateUpdatedEmail,
  dispatchVolunteerEventNotification
} from './emailService';
import { DEFAULT_EMAIL_SETTINGS } from './emailTemplates';
import { sanitizeFirestoreData } from '../utils/sanitizeFirestore';
import { addVolunteerNotification } from './notificationsService';
import { normalizeProductSizes, applySizeDeltaToSizes } from './inventoryService';

const COLLECTION_NAME = 'reservations';

export function subscribeReservations(
  onData: (reservations: Reservation[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const reservationsRef = collection(db, COLLECTION_NAME);
  return onSnapshot(
    reservationsRef,
    (snapshot) => {
      const reservations: Reservation[] = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...(docSnap.data() as Omit<Reservation, 'id'>)
      }));
      // Sort newest first
      reservations.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      onData(reservations);
    },
    (err) => {
      console.error('Error listening to reservations collection:', err);
      if (onError) onError(err);
    }
  );
}

export async function createReservation(
  reservationData: Omit<Reservation, 'id'>,
  emailSettings?: EmailSettings,
  siteSettings?: SiteSettings
): Promise<Reservation> {
  const newReservation = await runTransaction(db, async (transaction) => {
    // 1. Read product docs for all items in the reservation to check stock
    const itemReads = await Promise.all(
      reservationData.items.map(async (item) => {
        const prodRef = doc(db, 'products', item.productId);
        const prodSnap = await transaction.get(prodRef);
        return { item, prodRef, prodSnap };
      })
    );

    // 2. Validate stock for each item at the specific SIZE level
    for (const { item, prodSnap } of itemReads) {
      if (!prodSnap.exists()) {
        throw new Error(`Product "${item.productName}" is no longer available in catalogue.`);
      }
      const prodData = prodSnap.data() as Product;
      const currentSizes = normalizeProductSizes(prodData.sizes, prodData.size);
      const targetSizeName = (item.size || 'Standard').trim().toLowerCase();
      const matchedSize = currentSizes.find(
        (s) => s.size.trim().toLowerCase() === targetSizeName
      );
      const availableStock = matchedSize ? Math.max(0, Number(matchedSize.stock || 0)) : 0;

      if (availableStock < item.quantity) {
        throw new Error(
          `Insufficient stock for "${item.productName}" (Size: ${item.size}). Available: ${availableStock}, requested: ${item.quantity}.`
        );
      }
    }

    // 3. Deduct stock for each product strictly by size inside the transaction
    for (const { item, prodRef, prodSnap } of itemReads) {
      const prodData = prodSnap.data() as Product;
      const currentSizes = normalizeProductSizes(prodData.sizes, prodData.size);
      const updatedSizes = applySizeDeltaToSizes(currentSizes, item.size || 'Standard', -item.quantity);

      transaction.update(prodRef, sanitizeFirestoreData({
        sizes: updatedSizes,
        size: updatedSizes[0]?.size || 'Standard'
      }));
    }

    // 4. Create new reservation doc
    const newRef = doc(collection(db, COLLECTION_NAME));
    const created: Reservation = {
      ...reservationData,
      id: newRef.id,
      version: 1
    };
    transaction.set(newRef, sanitizeFirestoreData(created));

    return created;
  });

  // Automatically trigger Reservation Created Email
  try {
    const activeEmailSettings = emailSettings || DEFAULT_EMAIL_SETTINGS;
    const activeSiteSettings: SiteSettings = siteSettings || {
      heroHeading: '',
      heroSubheading: '',
      collectionLocation: 'Community Hub',
      collectionAddress: 'High Street Hub',
      openingHours: 'Mon-Fri 9am - 4pm',
      facebookUrl: '',
      contactEmail: 'support@uniformloop.org.uk',
      contactPhone: '01234 567890',
      reservationExpiryDays: 7
    };

    const orderRef = newReservation.reservationCode || newReservation.reservationNumber || newReservation.id || 'N/A';
    console.log(`[ORDER EMAIL] New order created: ${orderRef}`);
    console.log(`[ORDER EMAIL] Customer email recipient: ${newReservation.email}`);
    console.log(`[EMAIL TIMING] [1] Event occurred: new_order at ${new Date().toISOString()} (orderRef: ${orderRef})`);

    // In-app volunteer notification for new order activity - MUST RUN FIRST so badge & alerts update instantly!
    try {
      await addVolunteerNotification(
        newReservation.id,
        orderRef,
        'new_order',
        `📦 New reservation ${orderRef} submitted by ${newReservation.parentName}`
      );
      console.log(`[NOTIF] Successfully created volunteer notification for new order: ${orderRef}`);
    } catch (notifErr) {
      console.warn('Failed to issue volunteer new order notification:', notifErr);
    }

    // Customer transactional email (dispatched asynchronously - never blocks customer UI or volunteer notifications)
    sendReservationNotificationEmail(
      'created',
      newReservation,
      activeEmailSettings,
      activeSiteSettings
    ).catch((emailErr) => {
      console.error('Failed to send reservation confirmation email:', emailErr);
    });

    // Dispatch email notification to active subscribed volunteers (asynchronous)
    dispatchVolunteerEventNotification({
      eventType: 'new_order',
      reservation: newReservation,
      settings: activeEmailSettings
    }).catch((notifErr) => {
      console.warn('Volunteer new order notification dispatch caught error:', notifErr);
    });
  } catch (emailErr) {
    console.error('Failed to dispatch post-reservation events:', emailErr);
  }

  return newReservation;
}

// In-flight reservation mutation lock map (guarantees strictly one active mutation per reservation ID)
const inFlightReservationStatusLocks = new Map<string, Promise<void>>();

export async function updateReservationStatus(
  id: string,
  newStatus: ReservationStatus,
  emailSettings?: EmailSettings,
  siteSettings?: SiteSettings,
  volunteerName?: string
): Promise<void> {
  // Service-level in-flight lock: Coalesce concurrent calls for the exact same reservation ID
  if (inFlightReservationStatusLocks.has(id)) {
    console.warn(`[updateReservationStatus] Concurrent update detected for reservation ${id}. Reusing in-flight operation.`);
    return inFlightReservationStatusLocks.get(id)!;
  }

  const taskPromise = (async () => {
    let updatedReservation: Reservation | null = null;
    let oldStatus: ReservationStatus | null = null;

    const maxAttempts = 4;
    let lastError: any = null;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        await runTransaction(db, async (transaction) => {
          const resRef = doc(db, COLLECTION_NAME, id);
          const resSnap = await transaction.get(resRef);
          if (!resSnap.exists()) {
            throw new Error('Reservation not found');
          }

          const reservation = resSnap.data() as Reservation;
          oldStatus = reservation.status;

          if (oldStatus === newStatus) {
            updatedReservation = { ...reservation, id };
            return;
          }

        // Collected reservations are final historical records and cannot be reverted or cancelled
        if (oldStatus === 'Collected') {
          throw new Error('Collected reservations are completed historical records and cannot be altered or cancelled.');
        }

        const isCancelling = oldStatus !== 'Cancelled' && newStatus === 'Cancelled';
        const isUncancelling = oldStatus === 'Cancelled' && newStatus !== 'Cancelled';
        const isCollecting = newStatus === 'Collected';

        const validItems = (reservation.items || []).filter((item) => !!item.productId);

        // 1. ONLY read product documents if inventory stock needs to be modified (cancellation or restoration)
        // 2. ONLY read stats document if marking as Collected
        // Restricting reads strictly to mutated documents avoids OCC version conflicts on unrelated docs
        const prodReads = (isCancelling || isUncancelling)
          ? await Promise.all(
              validItems.map(async (item) => {
                const prodRef = doc(db, 'products', item.productId);
                const prodSnap = await transaction.get(prodRef);
                return { item, prodRef, prodSnap };
              })
            )
          : [];

        const statsDocRef = isCollecting ? doc(db, 'stats', 'impact') : null;
        const statsSnap = (isCollecting && statsDocRef) ? await transaction.get(statsDocRef) : null;

        // 2. Execute stock mutations based on status change (by SIZE)
        if (isCancelling) {
          // Cancellation: Return reserved items back to their respective sizes
          for (const { item, prodRef, prodSnap } of prodReads) {
            if (prodSnap.exists()) {
              const prodData = prodSnap.data() as Product;
              const currentSizes = normalizeProductSizes(prodData.sizes, prodData.size);
              const updatedSizes = applySizeDeltaToSizes(currentSizes, item.size || 'Standard', item.quantity || 1);

              transaction.update(prodRef, sanitizeFirestoreData({
                sizes: updatedSizes,
                size: updatedSizes[0]?.size || 'Standard'
              }));
            }
          }
        } else if (isUncancelling) {
          // Uncancelling/Restoring: Verify stock by size and re-reserve
          for (const { item, prodSnap } of prodReads) {
            if (prodSnap.exists()) {
              const prodData = prodSnap.data() as Product;
              const currentSizes = normalizeProductSizes(prodData.sizes, prodData.size);
              const targetSizeName = (item.size || 'Standard').trim().toLowerCase();
              const matchedSize = currentSizes.find(
                (s) => s.size.trim().toLowerCase() === targetSizeName
              );
              const availableStock = matchedSize ? Math.max(0, Number(matchedSize.stock || 0)) : 0;
              const neededQty = item.quantity || 1;

              if (availableStock < neededQty) {
                throw new Error(
                  `Cannot restore reservation: "${prodData.name}" (Size: ${item.size}) only has ${availableStock} in stock.`
                );
              }
            }
          }

          for (const { item, prodRef, prodSnap } of prodReads) {
            if (prodSnap.exists()) {
              const prodData = prodSnap.data() as Product;
              const currentSizes = normalizeProductSizes(prodData.sizes, prodData.size);
              const updatedSizes = applySizeDeltaToSizes(currentSizes, item.size || 'Standard', -(item.quantity || 1));

              transaction.update(prodRef, sanitizeFirestoreData({
                sizes: updatedSizes,
                size: updatedSizes[0]?.size || 'Standard'
              }));
            }
          }
        }

        // 3. Increment permanent lifetime stats when marked as Collected
        if (isCollecting && statsDocRef && statsSnap) {
          const itemsCollectedCount = (reservation.items || []).reduce(
            (sum, item) => sum + (item.quantity || 1),
            0
          );
          const currentStats: ImpactStats = statsSnap.exists()
            ? (statsSnap.data() as ImpactStats)
            : { familiesHelped: 0, itemsReused: 0, itemsDonated: 0, volunteers: 1, schoolsSupported: 1 };

          const newFamiliesHelped = (currentStats.familiesHelped || 0) + 1;
          const newItemsReused = (currentStats.itemsReused || 0) + itemsCollectedCount;
          const newItemsDonated = Math.max(
            currentStats.itemsDonated || 0,
            newItemsReused
          );

          transaction.set(
            statsDocRef,
            {
              familiesHelped: newFamiliesHelped,
              itemsReused: newItemsReused,
              itemsDonated: newItemsDonated
            },
            { merge: true }
          );
        }

        const actionText = newStatus === 'Being Prepared'
          ? 'Marked as Being Prepared'
          : newStatus === 'Ready for Collection'
          ? 'Marked Ready for Collection'
          : newStatus === 'Collected'
          ? 'Marked Collected'
          : newStatus === 'Cancelled'
          ? 'Reservation Cancelled'
          : `Status changed to ${newStatus}`;

        const auditEntry = {
          id: `audit-${Date.now()}`,
          timestamp: new Date().toISOString(),
          action: actionText,
          actor: 'volunteer' as const,
          details: `Status changed from "${oldStatus}" to "${newStatus}" by ${volunteerName || 'Volunteer'}.`,
          performedBy: volunteerName || 'Volunteer'
        };

        const auditTrail = [auditEntry, ...(reservation.auditTrail || [])];
        const nextVersion = (typeof reservation.version === 'number' ? reservation.version : 1) + 1;

        const updatePayload: Record<string, any> = {
          status: newStatus,
          version: nextVersion,
          lastUpdatedStatusAt: new Date().toISOString(),
          auditTrail
        };

        if (newStatus === 'Collected') {
          updatePayload.actualCollectedDate = new Date().toISOString();
        }

        transaction.update(resRef, sanitizeFirestoreData(updatePayload));
        updatedReservation = { ...reservation, id, ...updatePayload };
      });

      lastError = null;
      break;
    } catch (err: any) {
      lastError = err;
      const errMsg = (err?.message || String(err)).toLowerCase();
      const isConcurrencyConflict =
        errMsg.includes('does not match base version') ||
        errMsg.includes('aborted') ||
        errMsg.includes('failed-precondition') ||
        err?.code === 'failed-precondition' ||
        err?.code === 'aborted';

      if (isConcurrencyConflict && attempt < maxAttempts) {
        console.warn(`[updateReservationStatus] Concurrency conflict detected (attempt ${attempt}/${maxAttempts}). Retrying with authoritative state...`);
        await new Promise((resolve) => setTimeout(resolve, 80 * attempt + Math.random() * 80));
        continue;
      }
      throw err;
    }
  }

  if (lastError) {
    throw lastError;
  }

  // Send status update emails if status changed
  if (updatedReservation && oldStatus !== newStatus) {
    try {
      const activeEmailSettings = emailSettings || DEFAULT_EMAIL_SETTINGS;
      const activeSiteSettings: SiteSettings = siteSettings || {
        heroHeading: '',
        heroSubheading: '',
        collectionLocation: 'Community Hub',
        collectionAddress: 'High Street Hub',
        openingHours: 'Mon-Fri 9am - 4pm',
        facebookUrl: '',
        contactEmail: 'support@uniformloop.org.uk',
        contactPhone: '01234 567890',
        reservationExpiryDays: 7
      };

      if (newStatus === 'Being Prepared') {
        await sendReservationNotificationEmail('being_prepared', updatedReservation, activeEmailSettings, activeSiteSettings);
      } else if (newStatus === 'Ready for Collection') {
        await sendReservationNotificationEmail('ready', updatedReservation, activeEmailSettings, activeSiteSettings);
      } else if (newStatus === 'Collected') {
        await sendReservationNotificationEmail('collected', updatedReservation, activeEmailSettings, activeSiteSettings);
      } else if (newStatus === 'Cancelled') {
        await sendReservationNotificationEmail('cancelled', updatedReservation, activeEmailSettings, activeSiteSettings);
      }
      // Note: Routine volunteer status emails removed as per specification.
      // Status changes appear in Volunteer Hub & in-site notifications without sending volunteer emails.
    } catch (emailErr) {
      console.error('Failed to dispatch status update email:', emailErr);
    }
  }
  })();

  inFlightReservationStatusLocks.set(id, taskPromise);
  try {
    await taskPromise;
  } finally {
    inFlightReservationStatusLocks.delete(id);
  }
}

export async function deleteReservation(id: string): Promise<void> {
  await runTransaction(db, async (transaction) => {
    const resRef = doc(db, COLLECTION_NAME, id);
    const resSnap = await transaction.get(resRef);
    if (!resSnap.exists()) return;

    const reservation = resSnap.data() as Reservation;
    const validItems = (reservation.items || []).filter((item) => !!item.productId);

    // 1. READ ALL product snapshots FIRST before any writes or deletes
    const prodReads = await Promise.all(
      validItems.map(async (item) => {
        const prodRef = doc(db, 'products', item.productId);
        const prodSnap = await transaction.get(prodRef);
        return { item, prodRef, prodSnap };
      })
    );

    // 2. NOW execute size stock restoration if reservation was active
    if (reservation.status !== 'Cancelled' && reservation.status !== 'Collected') {
      for (const { item, prodRef, prodSnap } of prodReads) {
        if (prodSnap.exists()) {
          const prodData = prodSnap.data() as Product;
          const currentSizes = normalizeProductSizes(prodData.sizes, prodData.size);
          const updatedSizes = applySizeDeltaToSizes(currentSizes, item.size || 'Standard', item.quantity || 1);

          transaction.update(prodRef, sanitizeFirestoreData({
            sizes: updatedSizes,
            size: updatedSizes[0]?.size || 'Standard'
          }));
        }
      }
    }

    transaction.delete(resRef);
  });
}

/**
 * Customer Action: Update Telephone Number
 */
export async function updateReservationPhone(
  id: string,
  newPhone: string
): Promise<void> {
  const resRef = doc(db, COLLECTION_NAME, id);

  await runTransaction(db, async (tx) => {
    const resSnap = await tx.get(resRef);
    if (!resSnap.exists()) throw new Error('Reservation not found');

    const data = resSnap.data() as Reservation;
    const oldPhone = data.phone || 'None';

    const auditEntry = {
      id: `audit-${Date.now()}`,
      timestamp: new Date().toISOString(),
      action: 'Telephone Number Updated',
      actor: 'customer' as const,
      details: `Customer updated telephone number from "${oldPhone}" to "${newPhone}".`
    };

    const auditTrail = [...(data.auditTrail || []), auditEntry];
    const nextVersion = (typeof data.version === 'number' ? data.version : 1) + 1;

    tx.update(resRef, sanitizeFirestoreData({
      phone: newPhone,
      version: nextVersion,
      lastUpdatedPhoneAt: new Date().toISOString(),
      auditTrail
    }));
  });
}

/**
 * Customer Action: Add Note for Volunteers
 */
export async function addReservationCustomerNote(
  id: string,
  noteMessage: string,
  parentName?: string
): Promise<void> {
  const resRef = doc(db, COLLECTION_NAME, id);
  let updatedData: Reservation | null = null;
  let resCode = id;

  await runTransaction(db, async (tx) => {
    const resSnap = await tx.get(resRef);
    if (!resSnap.exists()) throw new Error('Reservation not found');

    const data = resSnap.data() as Reservation;
    resCode = data.reservationCode || data.reservationNumber || id;

    const newNote = {
      id: `note-${Date.now()}`,
      timestamp: new Date().toISOString(),
      message: noteMessage,
      authorName: parentName || data.parentName || 'Customer',
      isVolunteer: false
    };

    const auditEntry = {
      id: `audit-${Date.now()}`,
      timestamp: new Date().toISOString(),
      action: 'Customer Note Left',
      actor: 'customer' as const,
      details: `Customer added note: "${noteMessage}"`
    };

    const customerNotes = [newNote, ...(data.customerNotes || [])]; // Newest first
    const auditTrail = [auditEntry, ...(data.auditTrail || [])];   // Newest first
    const nextVersion = (typeof data.version === 'number' ? data.version : 1) + 1;

    tx.update(resRef, sanitizeFirestoreData({
      customerNotes,
      version: nextVersion,
      auditTrail
    }));

    updatedData = { ...data, id, customerNotes, auditTrail, version: nextVersion };
  });

  // Create Volunteer Notification (in-site alert only, no volunteer email per specification)
  try {
    await addVolunteerNotification(
      id,
      resCode,
      'note_added',
      `🔔 Reservation ${resCode} added a customer note: "${noteMessage.length > 60 ? noteMessage.substring(0, 60) + '...' : noteMessage}"`
    );
  } catch (err) {
    console.error('Failed to issue volunteer notification:', err);
  }
}

/**
 * Customer Action: Request Collection Date Change
 */
export async function requestReservationDateChange(
  id: string,
  newRequestedDate: string,
  reasonNotes?: string
): Promise<void> {
  const resRef = doc(db, COLLECTION_NAME, id);
  let updatedData: Reservation | null = null;
  let resCode = id;

  await runTransaction(db, async (tx) => {
    const resSnap = await tx.get(resRef);
    if (!resSnap.exists()) throw new Error('Reservation not found');

    const data = resSnap.data() as Reservation;
    resCode = data.reservationCode || data.reservationNumber || id;

    const previousStatus = (data.status && data.status !== 'Date Change Requested') ? data.status : 'Pending';
    const changeRequest: CollectionChangeRequest = {
      id: `req-${Date.now()}`,
      requestedDate: newRequestedDate,
      notes: reasonNotes || '',
      requestNotes: reasonNotes || '',
      requestedAt: new Date().toISOString(),
      previousStatus,
      status: 'Pending'
    };

    const auditEntry = {
      id: `audit-${Date.now()}`,
      timestamp: new Date().toISOString(),
      action: 'Date Change Requested',
      actor: 'customer' as const,
      details: `Customer requested date change to "${newRequestedDate}". ${reasonNotes ? `Notes: "${reasonNotes}"` : ''}`
    };

    const auditTrail = [auditEntry, ...(data.auditTrail || [])];

    // Primary status remains unchanged! Do NOT overwrite with 'Date Change Requested'
    tx.update(resRef, sanitizeFirestoreData({
      collectionChangeRequest: changeRequest,
      auditTrail
    }));

    updatedData = {
      ...data,
      id,
      collectionChangeRequest: changeRequest,
      auditTrail
    };
  });

  // Create Volunteer Notification
  try {
    await addVolunteerNotification(
      id,
      resCode,
      'date_change',
      `📅 Reservation ${resCode} requested collection date change to ${newRequestedDate}`
    );

    if (updatedData) {
      dispatchVolunteerEventNotification({
        eventType: 'date_change_request',
        reservation: updatedData,
        updateTitle: 'Collection Date Change Requested',
        updateDetails: `Customer requested change of collection date to "${newRequestedDate}". ${reasonNotes ? `Reason: "${reasonNotes}"` : ''}`
      }).catch(() => {});
    }
  } catch (err) {
    console.error('Failed to issue volunteer notification:', err);
  }
}

/**
 * Volunteer Action: Decide on Collection Date Change Request
 */
export async function decideCollectionDateChange(
  reservationOrId: string | Reservation,
  decision: 'Approved' | 'Declined' | 'Rejected',
  decisionNotes?: string,
  volunteerName: string = 'Volunteer',
  emailSettings?: EmailSettings,
  siteSettings?: SiteSettings
): Promise<void> {
  const id = typeof reservationOrId === 'string' ? reservationOrId : reservationOrId.id;
  const resRef = doc(db, COLLECTION_NAME, id);
  let updatedReservation: Reservation | null = null;
  const finalStatus: 'Approved' | 'Declined' = decision === 'Approved' ? 'Approved' : 'Declined';

  await runTransaction(db, async (tx) => {
    const resSnap = await tx.get(resRef);
    if (!resSnap.exists()) throw new Error('Reservation not found');

    const data = resSnap.data() as Reservation;
    const currentReq = data.collectionChangeRequest;
    if (!currentReq) throw new Error('No pending date change request found.');

    const updatedReq: CollectionChangeRequest = {
      ...currentReq,
      status: finalStatus,
      decisionNotes: decisionNotes || '',
      decidedAt: new Date().toISOString(),
      decidedBy: volunteerName
    };

    // Keep the reservation's existing primary status exactly as it was before the request.
    // If the reservation was 'Being Prepared', it stays 'Being Prepared'.
    // If it was 'Ready for Collection', it stays 'Ready for Collection'.
    // Do NOT automatically set the reservation to "Ready for Collection".
    const restoredStatus: ReservationStatus = (data.status && data.status !== 'Date Change Requested')
      ? data.status
      : (currentReq.previousStatus && currentReq.previousStatus !== 'Date Change Requested' ? currentReq.previousStatus : 'Pending');
    const newStatus: ReservationStatus = restoredStatus;
    const newDate = finalStatus === 'Approved' ? currentReq.requestedDate : data.preferredCollectionDay;

    const auditEntry = {
      id: `audit-${Date.now()}`,
      timestamp: new Date().toISOString(),
      action: `Date Change ${finalStatus}`,
      actor: 'volunteer' as const,
      details: `Date change request was ${finalStatus.toLowerCase()} by ${volunteerName}. ${finalStatus === 'Approved' ? `New collection date: "${newDate}".` : ''} ${decisionNotes ? `Notes: "${decisionNotes}"` : ''}`,
      performedBy: volunteerName
    };

    const auditTrail = [auditEntry, ...(data.auditTrail || [])];
    const nextVersion = (typeof data.version === 'number' ? data.version : 1) + 1;

    const updatePayload: Record<string, any> = {
      collectionChangeRequest: updatedReq,
      status: newStatus,
      preferredCollectionDay: newDate,
      version: nextVersion,
      auditTrail
    };

    tx.update(resRef, sanitizeFirestoreData(updatePayload));
    updatedReservation = {
      ...data,
      id,
      ...updatePayload
    };
  });

  // Trigger decision notification email
  if (updatedReservation) {
    try {
      await sendCollectionDateUpdatedEmail(
        updatedReservation,
        finalStatus,
        decisionNotes,
        emailSettings || DEFAULT_EMAIL_SETTINGS,
        volunteerName
      );
    } catch (emailErr) {
      console.error('Failed to dispatch collection date decision email:', emailErr);
    }
  }
}

export const decideReservationDateChange = decideCollectionDateChange;

/**
 * Customer Action: Cancel Reservation (before collection)
 */
export async function cancelReservationByCustomer(
  reservation: Reservation,
  cancellationReason?: string,
  emailSettings?: EmailSettings,
  siteSettings?: SiteSettings
): Promise<void> {
  if (reservation.status === 'Collected') {
    throw new Error('Collected reservations cannot be cancelled.');
  }

  const validItems = (reservation.items || []).filter((item) => !!item.productId);
  const resCode = reservation.reservationCode || reservation.reservationNumber || reservation.id;

  await runTransaction(db, async (transaction) => {
    const resRef = doc(db, COLLECTION_NAME, reservation.id);
    const resSnap = await transaction.get(resRef);
    if (!resSnap.exists()) throw new Error('Reservation not found');

    const freshRes = resSnap.data() as Reservation;
    if (freshRes.status === 'Collected') {
      throw new Error('Collected reservations cannot be cancelled.');
    }
    if (freshRes.status === 'Cancelled') {
      return;
    }

    // Restore stock quantities by specific size
    const prodReads = await Promise.all(
      validItems.map(async (item) => {
        const prodRef = doc(db, 'products', item.productId);
        const prodSnap = await transaction.get(prodRef);
        return { item, prodRef, prodSnap };
      })
    );

    for (const { item, prodRef, prodSnap } of prodReads) {
      if (prodSnap.exists()) {
        const prodData = prodSnap.data() as Product;
        const currentSizes = normalizeProductSizes(prodData.sizes, prodData.size);
        const updatedSizes = applySizeDeltaToSizes(currentSizes, item.size || 'Standard', item.quantity || 1);

        transaction.update(prodRef, sanitizeFirestoreData({
          sizes: updatedSizes,
          size: updatedSizes[0]?.size || 'Standard'
        }));
      }
    }

    const auditEntry = {
      id: `audit-${Date.now()}`,
      timestamp: new Date().toISOString(),
      action: 'Reservation Cancelled by Customer',
      actor: 'customer' as const,
      details: `Customer cancelled reservation. ${cancellationReason ? `Reason: "${cancellationReason}"` : ''}`
    };

    const auditTrail = [auditEntry, ...(freshRes.auditTrail || [])];
    const nextVersion = (typeof freshRes.version === 'number' ? freshRes.version : 1) + 1;

    transaction.update(resRef, sanitizeFirestoreData({
      status: 'Cancelled' as ReservationStatus,
      version: nextVersion,
      auditTrail
    }));
  });

  // Create Volunteer Notification
  try {
    await addVolunteerNotification(
      reservation.id,
      resCode,
      'cancelled',
      `🔔 Reservation ${resCode} cancelled by customer`
    );
  } catch (err) {
    console.error('Failed to issue volunteer notification:', err);
  }

  // Automatically dispatch cancellation confirmation email
  try {
    const activeEmailSettings = emailSettings || DEFAULT_EMAIL_SETTINGS;
    const activeSiteSettings = siteSettings || {
      heroHeading: '',
      heroSubheading: '',
      collectionLocation: 'Bert Boyden Community Centre',
      collectionAddress: '34 Carver Road, Immingham DN40 1DS',
      openingHours: 'Tue, Thu, Sat 10am - 2pm',
      facebookUrl: '',
      contactEmail: 'bert.boyden@virginmedia.com',
      contactPhone: '01469 570000',
      reservationExpiryDays: 7
    };

    const updatedRes: Reservation = {
      ...reservation,
      status: 'Cancelled'
    };

    await sendReservationNotificationEmail('cancelled', updatedRes, activeEmailSettings, activeSiteSettings);
  } catch (emailErr) {
    console.error('Failed to send cancellation confirmation email:', emailErr);
  }
}

/**
 * Volunteer Action: Mark Reservation as Collected (with actual collection date logging)
 */
export async function markReservationCollected(
  id: string,
  volunteerName: string = 'Volunteer',
  emailSettings?: EmailSettings,
  siteSettings?: SiteSettings
): Promise<void> {
  return updateReservationStatus(id, 'Collected', emailSettings, siteSettings, volunteerName);
}

/**
 * Checks if a collected or cancelled reservation is older than 30 days and should be auto-purged:
 * - Collected: 30 days from actualCollectedDate || preferredCollectionDay || createdAt
 * - Cancelled: 30 days from cancellation audit timestamp || lastUpdatedStatusAt || createdAt
 * - Active / Pending / Preparing / Ready / Date Change: NEVER purged.
 */
export function isReservationExpiredFromArchive(reservation: Reservation): boolean {
  const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

  if (reservation.status === 'Collected') {
    const dateStr = reservation.actualCollectedDate || reservation.preferredCollectionDay || reservation.createdAt;
    if (!dateStr) return false;
    const collectedTime = new Date(dateStr).getTime();
    if (isNaN(collectedTime)) return false;
    return Date.now() - collectedTime > THIRTY_DAYS_MS;
  }

  if (reservation.status === 'Cancelled') {
    // 1. Prefer actual cancellation timestamp from existing cancellation audit data
    let cancelDateStr: string | undefined;
    if (Array.isArray(reservation.auditTrail) && reservation.auditTrail.length > 0) {
      const cancelEntry = reservation.auditTrail.find(
        (entry) =>
          entry.action?.toLowerCase().includes('cancel') ||
          entry.details?.toLowerCase().includes('cancel')
      );
      if (cancelEntry?.timestamp) {
        cancelDateStr = cancelEntry.timestamp;
      }
    }

    // 2. Fall back to status update timestamp if present
    if (!cancelDateStr && (reservation as any).lastUpdatedStatusAt) {
      cancelDateStr = (reservation as any).lastUpdatedStatusAt;
    }

    // 3. Fall back to safest existing reservation date already used by current retention system
    if (!cancelDateStr) {
      cancelDateStr = reservation.createdAt || reservation.preferredCollectionDay;
    }

    if (!cancelDateStr) return false;
    const cancelTime = new Date(cancelDateStr).getTime();
    if (isNaN(cancelTime)) return false;
    return Date.now() - cancelTime > THIRTY_DAYS_MS;
  }

  return false;
}

/**
 * Automatically purges collected and cancelled reservations that are older than 30 days.
 * Lifetime statistics are stored separately in stats/impact and are NEVER reduced.
 */
export async function purgeExpiredArchivedReservations(reservations: Reservation[]): Promise<number> {
  const expired = reservations.filter(isReservationExpiredFromArchive);
  if (expired.length === 0) return 0;

  console.log(`[30-Day Archive Purge] Found ${expired.length} expired reservation(s) (collected/cancelled) older than 30 days. Purging...`);
  let purgedCount = 0;
  for (const r of expired) {
    try {
      await deleteReservation(r.id);
      purgedCount++;
    } catch (err) {
      console.error(`[30-Day Archive Purge] Failed to purge reservation ${r.id}:`, err);
    }
  }
  return purgedCount;
}
